import React from 'react';
import { useLocation, useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/Button';
import { CheckCircle, Printer, Download, ShoppingBag, Loader2 } from 'lucide-react';
import { useCartStore } from '../store/cart.store';
import { orderService, type Order } from '../services/order.service';

export const OrderConfirmationPage: React.FC = () => {
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const clearCart = useCartStore((s) => s.clearCart);

  const reference = searchParams.get('reference');
  const stateOrder = location.state?.order as Order | undefined;

  // Load the order either from router state or by id
  // (Paystack redirects to /order-confirmation/:id?reference=...)
  const { data: fetchedOrder, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderService.getOrder(parseInt(id!)),
    enabled: !!id,
  });

  const order = stateOrder ?? fetchedOrder;

  // Verify the payment when redirected back from the gateway with a reference
  const verifyPaymentMutation = useMutation({
    mutationFn: () => orderService.verifyPayment(parseInt(id!), reference!),
    onSuccess: () => {
      if (id) queryClient.invalidateQueries({ queryKey: ['order', id] });
    },
  });

  React.useEffect(() => {
    if (id && reference) {
      verifyPaymentMutation.mutate();
    }
  }, [id, reference]);

  // Clear the cart once the order is confirmed
  React.useEffect(() => {
    if (order) {
      clearCart();
    }
  }, [order, clearCart]);

  if (isLoading && !stateOrder) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h2 className="text-2xl font-bold text-[rgb(var(--foreground))]">No order found</h2>
        <Link to="/shop">
          <Button variant="primary" className="mt-4">Continue Shopping</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <div className="text-center mb-8">
        <div className="mx-auto h-20 w-20 rounded-full bg-green-500/10 flex items-center justify-center">
          <CheckCircle className="h-10 w-10 text-green-500" />
        </div>
        <h1 className="text-3xl font-bold text-[rgb(var(--foreground))]">Thank You!</h1>
        <p className="text-[rgb(var(--light-secondary))]">
          Your order #{order.orderNumber} has been placed successfully.
        </p>
      </div>

      <Card>
        <CardTitle>Order Summary</CardTitle>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-[rgb(var(--light-secondary))]">Order Number</span>
            <span className="font-mono">{order.orderNumber}</span>
            <span className="text-[rgb(var(--light-secondary))]">Date</span>
            <span>{new Date(order.createdAt).toLocaleString()}</span>
            <span className="text-[rgb(var(--light-secondary))]">Payment Method</span>
            <span>{order.paymentMethod || 'Paystack'}</span>
            <span className="text-[rgb(var(--light-secondary))]">Status</span>
            <span className="capitalize">{order.status?.toLowerCase()}</span>
          </div>

          <div className="border-t border-[rgb(var(--border))] pt-4">
            <h4 className="font-medium mb-2">Items</h4>
            {order.items?.map((item) => (
              <div key={item.id} className="flex justify-between text-sm py-1">
                <span>{item.productName} × {item.quantity}</span>
                <span>GHS {(item.total / 100).toFixed(2)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-[rgb(var(--border))] pt-4 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-[rgb(var(--light-secondary))]">Subtotal</span>
              <span>GHS {(order.subtotal / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[rgb(var(--light-secondary))]">Shipping</span>
              <span>{order.shippingCost === 0 ? 'Free' : `GHS ${(order.shippingCost / 100).toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[rgb(var(--light-secondary))]">Tax</span>
              <span>GHS {(order.tax / 100).toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg">
              <span>Total</span>
              <span className="text-primary">GHS {(order.total / 100).toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-6 flex flex-wrap gap-4 justify-center">
        <Button variant="secondary" onClick={() => window.print()}>
          <Printer className="mr-2 h-4 w-4" />
          Print Receipt
        </Button>
        <Button variant="secondary">
          <Download className="mr-2 h-4 w-4" />
          Download Invoice
        </Button>
        <Link to="/orders">
          <Button variant="primary">
            <ShoppingBag className="mr-2 h-4 w-4" />
            View My Orders
          </Button>
        </Link>
      </div>
    </div>
  );
};
